import { useCallback, useEffect, useRef, useState } from 'react';
import { activatePushOnDevice, PUSH_CHANGED_EVENT } from './pushDevice';
import { evaluatePushGate, type PushGateDecision } from './pushGate';

export type PushGateState = 'checking' | PushGateDecision;

/** Si la primera revisión no contesta en este tiempo, se deja usar la app (fail-open) y se sigue revisando por abajo. */
export const GATE_CHECK_TIMEOUT_MS = 8000;
/** Mínimo entre dos revisiones automáticas (volver a primer plano varias veces seguidas no satura al backend). */
export const GATE_MIN_INTERVAL_MS = 15000;

/**
 * Bloqueo de la app por notificaciones (ver `pushGate.ts`). Revalida al abrir, al volver a primer plano, al recuperar la
 * red, cuando el permiso cambia (donde el navegador lo avisa) y cuando la propia app activa/desactiva las push.
 * `recheck` fuerza una revisión inmediata (el botón "Ya lo activé"). La revisión de fondo NUNCA vuelve a mostrar "checking":
 * solo cambia de estado si el resultado cambió.
 */
export function usePushGate() {
  const [state, setState] = useState<PushGateState>('checking');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRun = useRef(0);
  const runId = useRef(0);
  const mounted = useRef(true);

  const run = useCallback(async (force: boolean) => {
    const now = Date.now();
    if (!force && now - lastRun.current < GATE_MIN_INTERVAL_MS) return;
    lastRun.current = now;
    const id = ++runId.current;
    const decision = await evaluatePushGate();
    // Una revisión más nueva ya empezó: la respuesta vieja no pisa a la nueva.
    if (mounted.current && id === runId.current) setState(decision);
  }, []);

  useEffect(() => {
    mounted.current = true;
    void run(true);
    // Fail-open: si la primera revisión se cuelga, no se deja a la psicóloga mirando un splash. Si después llega un
    // resultado que bloquea, se aplica.
    const timeout = setTimeout(() => setState((s) => (s === 'checking' ? 'open' : s)), GATE_CHECK_TIMEOUT_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void run(false);
    };
    const onOnline = () => void run(true);
    const onChanged = () => void run(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener(PUSH_CHANGED_EVENT, onChanged);

    // Donde el navegador avisa cuando cambia el permiso (Chrome/Android), se reacciona al instante.
    let permissionStatus: PermissionStatus | null = null;
    const onPermission = () => void run(true);
    navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then((status) => {
        if (!mounted.current) return;
        permissionStatus = status;
        status.addEventListener('change', onPermission);
      })
      .catch(() => {
        /* iOS/Safari no siempre soporta la consulta: queda la revisión al volver a primer plano */
      });

    return () => {
      mounted.current = false;
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener(PUSH_CHANGED_EVENT, onChanged);
      permissionStatus?.removeEventListener('change', onPermission);
    };
  }, [run]);

  /** "Activar notificaciones": pide el permiso y registra el dispositivo. */
  const enable = useCallback(async () => {
    setError(null);
    setWorking(true);
    try {
      const result = await activatePushOnDevice();
      if (result === 'denied') setState('denied');
      else if (result === 'off') setState('needs_enable');
      else await run(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos activar las notificaciones.');
      setState('needs_enable');
    } finally {
      if (mounted.current) setWorking(false);
    }
  }, [run]);

  /** "Ya lo activé": vuelve a mirar el permiso. */
  const recheck = useCallback(async () => {
    setError(null);
    setWorking(true);
    try {
      await run(true);
    } finally {
      if (mounted.current) setWorking(false);
    }
  }, [run]);

  return { state, working, error, enable, recheck };
}
